"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { formatSavedAt, weekdayUk } from "@/lib/kyiv";
import { describeMark } from "@/lib/marks";
import { givenName } from "@/lib/names";
import { absentWord, givenInCase } from "@/lib/child-grammar";
import type { DailyMarkDto, MeResponse, TodayResponse } from "@/lib/types";
import { cn } from "@/lib/utils";
import { StatusScreen } from "@/components/status-screen";
import { SmoothReveal } from "@/components/smooth-reveal";

function initialChoice(row?: DailyMarkDto | null) {
  if (row?.responded) {
    return {
      absent: row.absent,
      breakfast: row.absent ? null : row.breakfast,
      afterschool: row.absent ? null : row.afterschool,
    };
  }
  if (row?.suggested) {
    return {
      absent: row.suggested.absent,
      breakfast: row.suggested.absent ? null : row.suggested.breakfast,
      afterschool: row.suggested.absent ? null : row.suggested.afterschool,
    };
  }
  return { absent: null, breakfast: null, afterschool: null };
}

function noteFromRow(row?: DailyMarkDto | null) {
  if (row?.responded) return row.note;
  return row?.suggested?.note ?? "";
}

function myRows(today: TodayResponse | null | undefined, childIds: string[]) {
  if (!today) return [];
  return childIds
    .map((id) => today.children.find((row) => row.childId === id))
    .filter((row): row is DailyMarkDto => Boolean(row));
}

function Choice({
  label,
  yesLabel,
  noLabel,
  value,
  onChange,
}: {
  label: string;
  yesLabel: string;
  noLabel: string;
  value: boolean | null;
  onChange: (next: boolean) => void;
}) {
  const idle = "bg-accent text-foreground hover:bg-accent/80";
  const selected = "bg-primary text-primary-foreground shadow-sm";
  return (
    <section className="rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
      <h2 className="text-base font-semibold">{label}</h2>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={cn("h-14 rounded-2xl text-base font-semibold transition wide:h-11", value === true ? selected : idle)}
        >
          {yesLabel}
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={cn("h-14 rounded-2xl text-base font-semibold transition wide:h-11", value === false ? selected : idle)}
        >
          {noLabel}
        </button>
      </div>
    </section>
  );
}

type FormChild = { id: string; name: string; gender: DailyMarkDto["gender"] };

export function DailyForm({
  me,
  today,
  onSaved,
  forChildren,
  assist = false,
}: {
  me: MeResponse;
  today: TodayResponse;
  onSaved?: () => void;
  forChildren?: FormChild[];
  assist?: boolean;
}) {
  const pool = forChildren ?? me.children;
  const firstId = pool[0]?.id ?? "";
  const initialRow = today.children.find((row) => row.childId === firstId);
  const [childId, setChildId] = useState(firstId);
  const [absent, setAbsent] = useState<boolean | null>(() => initialChoice(initialRow).absent);
  const [breakfast, setBreakfast] = useState<boolean | null>(() => initialChoice(initialRow).breakfast);
  const [afterschool, setAfterschool] = useState<boolean | null>(() => initialChoice(initialRow).afterschool);
  const [note, setNote] = useState(() => noteFromRow(initialRow));
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(assist);

  const current = useMemo(
    () => today.children.find((row) => row.childId === childId) ?? null,
    [today, childId],
  );

  function applyRow(row?: DailyMarkDto) {
    const choice = initialChoice(row);
    setAbsent(choice.absent);
    setBreakfast(choice.breakfast);
    setAfterschool(choice.afterschool);
    setNote(noteFromRow(row));
  }

  useEffect(() => {
    const first = pool[0]?.id ?? "";
    const selected = pool.some((child) => child.id === childId) ? childId : first;
    setChildId(selected);
    applyRow(today.children.find((item) => item.childId === selected));
    setEditing(assist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today.date, assist, pool.map((child) => child.id).join("|")]);

  async function save() {
    if (!childId || absent === null) {
      toast.error(`Потрібно сказати, чи ${givenInCase(current?.childName ?? "дитина", current?.gender ?? "girl", "nom")} буде в школі.`);
      return;
    }
    if (!absent && (breakfast === null || afterschool === null)) {
      toast.error("Потрібно відповісти про сніданок і групу.");
      return;
    }
    setSaving(true);
    try {
      await api("/api/today", {
        method: "POST",
        body: JSON.stringify({
          childId,
          absent,
          breakfast: absent ? false : breakfast,
          afterschool: absent ? false : afterschool,
          note,
        }),
      });
      toast.success(assist ? "Збережено замість батьків." : "Збережено. Це вже в огляді дня.");
      setEditing(false);
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти.");
    } finally {
      setSaving(false);
    }
  }

  async function copyFrom(source: "previous" | "week") {
    if (!childId) return;
    setSaving(true);
    try {
      const result = await api<{ applied: Array<{ name: string; summary: string }> }>("/api/today/copy", {
        method: "POST",
        body: JSON.stringify({ source, childId }),
      });
      const copiedAs =
        source === "week" ? "як минулого тижня" : today.eveningOpen ? "як сьогодні" : "як учора";
      toast.success(
        result.applied.length === 1
          ? `Поставлено ${copiedAs}.`
          : `Поставлено ${copiedAs}: ${result.applied.map((item) => item.name).join(", ")}.`,
      );
      setEditing(false);
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося скопіювати відмітку.");
    } finally {
      setSaving(false);
    }
  }

  if (pool.length === 0) {
    return (
      <StatusScreen
        title="Вас ще не прив’язали до дитини"
        text="Адміністратор вручну вказує, хто чиї батьки. Одна дитина може мати кількох батьків. Напишіть адміністратору, якщо це помилка."
      />
    );
  }

  const savedRows = myRows(
    today,
    pool.map((child) => child.id),
  );
  const allDone = savedRows.length > 0 && savedRows.every((row) => row.responded);

  const showForm = assist || !allDone || editing;
  const weekday = weekdayUk(today.date);
  const previousLabel = today.eveningOpen ? "Як сьогодні" : "Як учора";

  return (
    <div>
      {!assist && allDone ? (
        <section className="rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">Відмітка на {today.label}</h2>
              <ul className="mt-2 space-y-1 text-sm leading-6">
                {savedRows.map((row) => (
                  <li key={row.childId}>
                    <span className="font-medium">{givenName(row.childName)}</span>
                    {" — "}
                    {describeMark({ ...row, note: "" }, row.gender)}
                    {row.note ? (
                      <span className="block text-muted-foreground">{row.note}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              className="shrink-0 text-sm font-medium text-primary"
              onClick={() => {
                if (!editing) {
                  applyRow(today.children.find((row) => row.childId === childId));
                }
                setEditing((next) => !next);
              }}
            >
              {editing ? "Згорнути" : "Змінити"}
            </button>
          </div>
        </section>
      ) : null}
      <SmoothReveal open={showForm}>
        <div className={cn("space-y-4", !assist && allDone && "pt-4")}>
      {assist ? (
        <p className="rounded-2xl bg-card px-4 py-3 text-sm leading-6 ring-1 ring-foreground/8">
          Відмітка замість батьків. Вони побачать її в додатку, окреме повідомлення не йде.
        </p>
      ) : today.eveningOpen ? (
        <p className="rounded-2xl bg-card px-4 py-3 text-sm leading-6 ring-1 ring-foreground/8">
          Класний керівник побачить це вранці до {me.settings.deadlineTime}.
        </p>
      ) : today.pastDeadline ? (
        <p className="rounded-2xl bg-accent px-4 py-3 text-sm leading-6">
          Дедлайн {me.settings.deadlineTime} уже минув. Відмітку все ще можна змінити, але класний керівник міг уже подивитись огляд дня.
        </p>
      ) : (
        <p className="rounded-2xl bg-card px-4 py-3 text-sm leading-6 ring-1 ring-foreground/8">
          Відмітьте ранок до {me.settings.deadlineTime}. Це замінить повідомлення в чаті.
        </p>
      )}
      {today.editable && current && !current.responded ? (
        <div className="space-y-2 rounded-2xl bg-accent px-4 py-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={saving || !current.suggested}
              onClick={() => void copyFrom("previous")}
              className={cn(
                "h-11 rounded-2xl text-sm font-semibold",
                current.suggested
                  ? "bg-card text-foreground ring-1 ring-foreground/10"
                  : "bg-card/60 text-muted-foreground",
              )}
            >
              {previousLabel}
            </button>
            <button
              type="button"
              disabled={saving || !current.weekSuggested}
              onClick={() => void copyFrom("week")}
              className={cn(
                "h-11 rounded-2xl text-sm font-semibold",
                current.weekSuggested
                  ? "bg-card text-foreground ring-1 ring-foreground/10"
                  : "bg-card/60 text-muted-foreground",
              )}
            >
              Як минулого тижня
            </button>
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            Минулий тиждень — {weekday} як {weekday}, бо гуртки часто ті самі.
          </p>
          {current.suggested ? (
            <p className="text-sm leading-5">
              {previousLabel}: {describeMark(current.suggested, current.gender)}.
            </p>
          ) : null}
          {current.weekSuggested ? (
            <p className="text-sm leading-5">
              Як минулого тижня: {describeMark(current.weekSuggested, current.gender)}.
            </p>
          ) : null}
        </div>
      ) : null}

      {pool.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {pool.map((child) => (
            <button
              key={child.id}
              type="button"
              onClick={() => {
                setChildId(child.id);
                applyRow(today.children.find((row) => row.childId === child.id));
              }}
              className={cn(
                "rounded-full px-3 py-1.5 text-sm whitespace-nowrap",
                childId === child.id ? "bg-primary text-primary-foreground" : "bg-accent",
              )}
            >
              {givenName(child.name)}
            </button>
          ))}
        </div>
      ) : null}

      <Choice
        label="У школі цього ранку"
        yesLabel="Буде"
        noLabel={absentWord(current?.gender ?? "girl", true)}
        value={absent === null ? null : !absent}
        onChange={(present) => {
          setAbsent(!present);
          if (!present) {
            setBreakfast(null);
            setAfterschool(null);
          }
        }}
      />

      <div>
      <SmoothReveal open={absent === false}>
        <div className="space-y-4">
          <Choice
            label="Сніданок"
            yesLabel="Буде"
            noLabel="Не буде"
            value={breakfast}
            onChange={setBreakfast}
          />
          <Choice
            label="Група продовженого дня"
            yesLabel="Залишається"
            noLabel="Йдемо додому"
            value={afterschool}
            onChange={setAfterschool}
          />
        </div>
      </SmoothReveal>
      <SmoothReveal open={absent === true}>
        <p className="rounded-2xl bg-accent px-4 py-3 text-sm leading-6">
          {givenInCase(current?.childName ?? "Дитина", current?.gender ?? "girl", "nom")} цього ранку не буде в школі. Сніданок і група не потрібні.
        </p>
      </SmoothReveal>
      </div>

      <section className="rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
        <label htmlFor="note" className="text-base font-semibold">
          Примітка
        </label>
        <p className="mt-1 text-sm text-muted-foreground">
          {absent === true
            ? "Необов’язково. Наприклад, «хвороба» або «сімейні обставини»."
            : "Необов’язково. Наприклад, «заберуть о 15:00»."}
        </p>
        <Textarea
          id="note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Коротко, якщо є що сказати класному керівнику"
          className="mt-3 min-h-24 rounded-2xl"
        />
      </section>

      <Button
        size="lg"
        className="h-12 w-full rounded-2xl text-base"
        onClick={() => void save()}
        disabled={saving}
      >
        {saving ? "Зберігаємо…" : current?.responded ? "Оновити відмітку" : "Зберегти на цей ранок"}
      </Button>
      {current?.updatedAt ? (
        <p className="text-center text-xs text-muted-foreground">
          Останнє збереження о {formatSavedAt(current.updatedAt)}
        </p>
      ) : null}
        </div>
      </SmoothReveal>
    </div>
  );
}
